import {
  AdjustmentsIcon,
  ArrowSmRightIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleRightIcon,
  InformationCircleIcon,
  LockClosedIcon,
  LockOpenIcon,
  MinusIcon,
  PaperAirplaneIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  SearchIcon,
  StarIcon,
  TrashIcon,
  TrendingUpIcon,
  UserAddIcon,
  VideoCameraIcon,
  ViewGridIcon,
} from "@heroicons/react/outline";
import { ExclamationIcon, XIcon } from "@heroicons/react/solid";
import {
  Activity,
  AlignBottom01,
  AlignTop01,
  ArrowLeft,
  ArrowNarrowRight,
  ArrowRight,
  Asterisk01,
  BarChartSquare01,
  BatteryMid,
  BookOpen01,
  Calendar,
  CalendarCheck02,
  CalendarPlus02,
  CheckSquare,
  ChevronDown,
  ChevronLeft,
  ChevronLeftDouble,
  ChevronRight,
  ChevronUp,
  Circle,
  Clipboard,
  ClockRewind,
  ClockStopwatch,
  Compass03,
  Copy07,
  CpuChip01,
  CreditCard02,
  Dataflow03,
  DotsHorizontal,
  Edit05,
  Expand01,
  File04,
  FileSearch01,
  FilterFunnel01,
  Globe02,
  Hand,
  HeartHand,
  Home02,
  InfoCircle,
  LayersTwo01,
  LayoutGrid01,
  LayoutLeft,
  LayoutRight,
  Lightbulb02,
  Lightning02,
  LineChartUp01,
  Link01,
  LinkExternal01,
  Menu03,
  MessageChatCircle,
  MessageDotsSquare,
  MessageSquare02,
  Microphone01,
  MicrophoneOff01,
  Minimize01,
  NotificationBox,
  PauseCircle,
  PlusCircle,
  RefreshCcw02,
  Rocket02,
  Route,
  Rows01,
  Scales02,
  Server01,
  Settings02,
  Share01,
  Shield01,
  Sliders04,
  Star04,
  SwitchHorizontal01,
  Target05,
  ThumbsDown,
  ThumbsUp,
  Trophy01,
  Upload04,
  User01,
  UserCircle,
  UserPlus01,
  UserPlus02,
  Users01,
  UsersCheck,
  UsersRight,
  Zap,
} from "@untitled-ui/icons-react";
import { SVGProps } from "react";
import { IconType } from "react-icons";
import { BiCommentAdd } from "react-icons/bi";
import { FaArrowDown, FaArrowUp, FaEquals } from "react-icons/fa6";
import { GoArrowDownRight, GoArrowUpRight } from "react-icons/go";
import {
  HiDotsHorizontal,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineSwitchHorizontal,
} from "react-icons/hi";
import { IoMdExit, IoMdHappy } from "react-icons/io";
import { LiaUserEditSolid } from "react-icons/lia";
import {
  MdCheckBoxOutlineBlank,
  MdContentCopy,
  MdFilterAlt,
  MdOutlineCancel,
  MdOutlineChat,
  MdOutlineCheckBox,
  MdOutlineDragIndicator,
  MdRadioButtonChecked,
  MdRadioButtonUnchecked,
} from "react-icons/md";
import { PiTextAlignLeftBold } from "react-icons/pi";
import { RiTeamLine } from "react-icons/ri";
import { RxDotFilled } from "react-icons/rx";
import { TbBuilding, TbLayoutSidebarFilled } from "react-icons/tb";

import CheckSquareEmpty from "./icons/check-square-empty";
import Progress0 from "./icons/progress-0";
import Progress25 from "./icons/progress-25";
import Progress50 from "./icons/progress-50";
import Progress75 from "./icons/progress-75";
import Progress100 from "./icons/progress-100";

export const TopicflowIconMap = {
  actionItem: MdCheckBoxOutlineBlank,
  // Square checkbox pair used by action-item complete toggles. `checkSquare`
  // comes from untitled-ui; `checkSquareEmpty` is a local SVG sibling that
  // matches its stroke style for the unchecked state.
  checkSquare: CheckSquare,
  checkSquareEmpty: CheckSquareEmpty,
  // Progress ring icons used by goal progress chips. Five buckets — 0, 25,
  // 50, 75, 100 — picked based on the goal's `progress` and `state` values.
  progress0: Progress0,
  progress25: Progress25,
  progress50: Progress50,
  progress75: Progress75,
  progress100: Progress100,
  activity: Activity,
  adjustments: AdjustmentsIcon,
  alignBottom: AlignBottom01,
  alignTop: AlignTop01,
  ai: Star04,
  agent: CpuChip01,
  arrowLeft: ArrowLeft,
  arrowNarrowRight: ArrowNarrowRight,
  arrowRight: ArrowRight,
  assessment: BarChartSquare01,
  asterisk: Asterisk01,
  calendar: Calendar,
  calendarCheck: CalendarCheck02,
  calendarPlus: CalendarPlus02,
  chat: MdOutlineChat,
  chatAdd: BiCommentAdd,
  check: CheckIcon,
  checkCircle: CheckCircleIcon,
  circle: Circle,
  chevronLeft: ChevronLeft,
  chevronLeftDouble: ChevronLeftDouble,
  chevronRight: ChevronRight,
  chevronDown: ChevronDown,
  chevronDownDouble: ChevronDoubleDownIcon,
  chevronRightDouble: ChevronDoubleRightIcon,
  chevronUp: ChevronUp,
  close: XIcon,
  copy: MdContentCopy,
  "copy-07": Copy07,
  "book-open-01": BookOpen01,
  creditCard: CreditCard02,
  dataflow: Dataflow03,
  deactivated: MdOutlineCancel,
  dot: RxDotFilled,
  downArrow: FaArrowDown,
  downRightArrow: GoArrowDownRight,
  drag: MdOutlineDragIndicator,
  edit: PencilIcon,
  equals: FaEquals,
  exit: IoMdExit,
  collapseArrows: Minimize01,
  expandArrows: Expand01,
  explorer: Compass03,
  externalLink: LinkExternal01,
  eye: HiOutlineEye,
  eyeOff: HiOutlineEyeOff,
  feedback: Upload04,
  upload: Upload04,
  file04: File04,
  fileSearch: FileSearch01,
  filter: MdFilterAlt,
  filterFunnel: FilterFunnel01,
  goal: Target05,
  home: Home02,
  horizontalDots: DotsHorizontal,
  info: InformationCircleIcon,
  infoCircle: InfoCircle,
  kpi: TrendingUpIcon,
  layersTwo: LayersTwo01,
  layoutLeft: LayoutLeft,
  link: Link01,
  lock: LockOpenIcon,
  lockClosed: LockClosedIcon,
  meeting: Calendar,
  menu03: Menu03,
  minus: MinusIcon,

  MessageSquare02: MessageSquare02,
  messagesDotsSquare: MessageDotsSquare,
  microphone: Microphone01,
  microphoneOff: MicrophoneOff01,
  paperAirplane: PaperAirplaneIcon,
  multichoice: MdOutlineCheckBox,
  nps: IoMdHappy,
  organization: TbBuilding,
  pauseCircle: PauseCircle,
  pencilSquare: Edit05,
  play: PlayIcon,
  plus: PlusIcon,
  plusCircle: PlusCircle,
  overdue: ClockStopwatch,
  previous: ClockRewind,
  program: LayoutGrid01,
  radioButtonChecked: MdRadioButtonChecked,
  radioButtonUnchecked: MdRadioButtonUnchecked,
  range: HiDotsHorizontal,
  recognition: Trophy01,
  refresh: RefreshCcw02,
  reporting: LineChartUp01,
  rightArrow: ArrowSmRightIcon,
  route: Route,
  rows: Rows01,
  search: SearchIcon,
  server: Server01,
  settings02: Settings02,
  share: Share01,
  sidebar: TbLayoutSidebarFilled,
  toggleSidebar: LayoutRight,
  star: StarIcon,
  survey: Clipboard,
  switch: HiOutlineSwitchHorizontal,
  switchHorizontal: SwitchHorizontal01,
  team: RiTeamLine,
  text: PiTextAlignLeftBold,
  threads: MessageSquare02,
  thumbsDown: ThumbsDown,
  thumbsUp: ThumbsUp,
  trash: TrashIcon,
  today: NotificationBox,
  upArrow: FaArrowUp,
  upRightArrow: GoArrowUpRight,
  globe02: Globe02,
  user: User01,
  "user-circle": UserCircle,
  userAdd: UserAddIcon,
  userPlus01: UserPlus01,
  userPlus02: UserPlus02,
  userEdit: LiaUserEditSolid,
  users: Users01,
  videoCamera: VideoCameraIcon,
  viewGrid: ViewGridIcon,
  usersCheck: UsersCheck,
  usersRight: UsersRight,
  warning: ExclamationIcon,

  // Coaching dimension icons (keyed by dimension name)
  "Career Growth": Rocket02,
  "Blockers & Support": Lightning02,
  "Blockers Support": Lightning02,
  "Workload Balance": Scales02,
  "Psychological Safety": Shield01,
  "Feedback & Recognition": Trophy01,
  "Feedback Recognition": Trophy01,
  "Decision Making": Zap,
  "Autonomy & Delegation": Hand,
  "Autonomy Delegation": Hand,
  "Energy Alignment": BatteryMid,
  "Frequency Health": Calendar,
  "Meeting Structure": Sliders04,
  "Talk Ratio": MessageChatCircle,
  "Personal Connection": HeartHand,
  "Expectations Tracker": Target05,
  "Team Dynamics": Users01,
  coachingDefault: Lightbulb02,
};

export type TopicflowIconType = keyof typeof TopicflowIconMap;

export const getTopicflowIcon = (
  type: TopicflowIconType,
): ((props: SVGProps<SVGSVGElement>) => React.JSX.Element) | IconType => {
  if (type in TopicflowIconMap) {
    return TopicflowIconMap[type];
  }
  throw new Error(`Unknown icon type: ${type}`);
};
